import type { CalendarEvent } from "../models/calendar.model"
import { getEventPalette } from "../utils/colorPalette"
import { useEventPopover } from "../hooks/useEventPopover"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function isSameMonth(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

function getGridDays(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1).getDay()
  const days: Date[] = []
  for (let i = 0; i < firstDay; i++) {
    const d = new Date(year, month, -firstDay + i + 1)
    days.push(d)
  }
  const total = new Date(year, month + 1, 0).getDate()
  for (let d = 1; d <= total; d++) days.push(new Date(year, month, d))
  const remaining = 42 - days.length
  for (let i = 1; i <= remaining; i++) days.push(new Date(year, month + 1, i))
  return days
}

interface Props {
  currentDate: Date
  events: CalendarEvent[]
  onNavigateDay?: (date: Date) => void
}

export default function MonthView({ currentDate, events, onNavigateDay }: Props) {
  const t = useTranslation()
  const today = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const gridDays = getGridDays(year, month)
  const { activeEventId, openPopover, closePopover, popoverPortal } = useEventPopover()

  const getEventsForDay = (date: Date) =>
    events.filter(e => sameDay(e.date, date)).sort((a, b) => a.startTime.localeCompare(b.startTime))

  return (
    <div className="flex flex-col h-full" onClick={closePopover}>
      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 border-b border-border shrink-0">
        {DAY_LABELS.map(d => (
          <div key={d} className="py-2 text-center text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="flex-1 grid grid-cols-7 grid-rows-6 overflow-hidden">
        {gridDays.map((date, idx) => {
          const isThisMonth = isSameMonth(date, currentDate)
          const isToday = sameDay(date, today)
          const dayEvents = getEventsForDay(date)
          const maxShow = 2
          const overflow = dayEvents.length - maxShow

          return (
            <div
              key={idx}
              className={`
                border-b border-r border-border
                flex flex-col p-1 min-h-0 overflow-hidden transition-colors relative
                ${isThisMonth ? "bg-card" : "bg-muted/20"}
                hover:bg-muted/30
              `}
            >
              {/* Date number + Badge */}
              <div className="shrink-0 mb-1 flex justify-between items-center px-0.5">
                <span
                  className={`
                    w-7 h-7 flex items-center justify-center rounded-full text-xs font-medium transition-colors
                    ${isToday
                      ? "bg-primary text-white"
                      : isThisMonth
                        ? "text-foreground"
                        : "text-muted-foreground/40"
                    }
                  `}
                >
                  {date.getDate()}
                </span>

                {overflow > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onNavigateDay?.(date)
                    }}
                    className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold hover:bg-primary/20 transition-colors shrink-0"
                    title={t('calendar.viewMoreEvents').replace('{count}', String(overflow))}
                  >
                    +{overflow}
                  </button>
                )}
              </div>

              {/* Event chips */}
              <div className="flex-1 overflow-hidden space-y-0.5 min-h-0">
                {dayEvents.slice(0, maxShow).map(ev => {
                  const palette = getEventPalette(ev)
                  const isActive = activeEventId === ev.id
                  const isDarkMode = document.documentElement.classList.contains('dark')
                  return (
                    <div key={ev.id} className="relative">
                      <button
                        onClick={e => {
                          e.stopPropagation()
                          if (isActive) {
                            closePopover()
                          } else {
                            openPopover(ev, e.currentTarget)
                          }
                        }}
                        className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] font-medium truncate transition-opacity hover:opacity-90 ${isActive ? "ring-2 ring-offset-1 ring-primary/80" : ""
                          }`}
                        style={{
                          background: isDarkMode ? palette.darkLight : palette.light,
                          borderLeft: `3px solid ${palette.solid}`,
                          color: isDarkMode ? palette.darkText : palette.text,
                        }}
                      >
                        {ev.title}
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
      {popoverPortal}
    </div>
  )
}
